import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import useStore from "../../store";
import Modal from "react-modal";
import OrderInvoice from "./OrderInvoice";
import useTranslation from "../../hooks/useTranslation";
import "./style.scss";

const money = (n) =>
  Number(n || 0)
    .toLocaleString("uz-UZ")
    .replace(/\u00A0/g, " ");

const pickNum = (row, keys) => {
  for (const k of keys) {
    const v = row?.[k];
    if (typeof v === "number" && isFinite(v)) return v;
    if (typeof v === "string" && v.trim() !== "" && isFinite(+v)) return +v;
  }
  return 0;
};

const statusInfo = (row, tr) => {
  const raw = String(
    row?.status ||
      row?.state ||
      row?.order_state ||
      row?.status_name ||
      "pending",
  ).toLowerCase();

  if (/(new|pending|ожид|kutish)/.test(raw))
    return { key: "pending", label: tr("ожидается") };
  if (/(done|finished|completed|yopildi|готов)/.test(raw))
    return { key: "done", label: tr("готов") };
  if (/(cancel|отмен|bekor)/.test(raw))
    return { key: "cancel", label: tr("отменён") };
  if (/(courier|в пути|yetkazilmoqda)/.test(raw))
    return { key: "way", label: tr("в пути") };
  return { key: "info", label: raw };
};

const normalize = (row, tr) => ({
  id: row.id,
  number:
    row.order_number || row.number || row.table_number || row.check || row.id,
  total: pickNum(row, ["price", "total", "sum", "amount"]),
  paid_amount: pickNum(row, ["paid_amount"]),
  cashier:
    row.cashier_name || row.cashier || row.created_user_name || tr("кассир"),
  courier: row.courier_name || row.courier || row.deliveryman || null,
  time:
    (row.created_at || row.date || row.created_date || "")
      .toString()
      .slice(11, 16) || "",
  status: statusInfo(row, tr),
});

const toneClass = (key) => {
  if (key === "pending") return "card--warning";
  if (key === "done") return "card--success";
  if (key === "cancel") return "card--danger";
  if (key === "way") return "card--info";
  return "card--neutral";
};

const Orders = () => {
  const { id } = useParams();
  const { setCompany, setDeliverOrders, setShowInvoice, showInvoice } =
    useStore((st) => st);
  const { tr } = useTranslation();
  const [orderData, setOrderData] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [activeCompanyId, setActiveCompanyId] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadCompanies = () =>
    axios
      .get(`${process.env.REACT_APP_API_URL}online-menu/${id}/companies`)
      .then(({ data }) => {
        const list = data?.data ?? data ?? [];
        const first = Array.isArray(list) ? list[0] : list;
        setCompanies(Array.isArray(list) ? list : first ? [first] : []);
        if (first?.id || first?.company_id) {
          setCompany(first);
          setActiveCompanyId(first.id ?? first.company_id);
        }
      });

  const loadOrders = (companyId) =>
    axios
      .get(`${process.env.REACT_APP_API_URL}online-menu/orders/${companyId}`, {
        params: { per_page: 100 },
      })
      .then(({ data }) => {
        const list = data?.data ?? data?.results ?? data ?? [];
        setOrders(Array.isArray(list) ? list : []);
        setDeliverOrders(list);
      });

  useEffect(() => {
    setLoading(true);
    if (id) {
      loadCompanies()
        .catch(console.log)
        .finally(() => setLoading(false));
    }
  }, [id]);

  useEffect(() => {
    if (!activeCompanyId) return;
    setLoading(true);
    loadOrders(activeCompanyId)
      .catch(console.log)
      .finally(() => setLoading(false));
  }, [activeCompanyId]);

  const rows = useMemo(
    () => orders.map((order) => normalize(order, tr)),
    [orders, tr],
  );

  return (
    <div className="orders-wrap">
      {companies.length > 1 && (
        <div className="tabs">
          {companies.map((c) => {
            const cid = c.id ?? c.company_id;
            const name = c.name ?? c.company_name ?? `#${cid}`;
            const active = cid === activeCompanyId;
            return (
              <button
                key={cid}
                onClick={() => setActiveCompanyId(cid)}
                className={`tab ${active ? "tab--active" : "tab--muted"}`}
                title={name}
              >
                {name}
              </button>
            );
          })}
        </div>
      )}

      {loading && (
        <div className="skel-grid">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="skel-card" />
          ))}
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div className="empty">{tr("Заказы отсутствуют")}</div>
      )}

      {!loading && rows.length > 0 && (
        <div className="grid">
          {rows.map((o) => (
            <article
              key={o.id}
              className={`card ${toneClass(o.status.key)}`}
              onClick={() => {
                setOrderData(o);
                setShowInvoice(true);
              }}
            >
              <div className="card__top">
                <div className="card__number">№ {o.number}</div>

                <div className="card__total">
                  <p>{money(o.total)}</p>

                  {Number(o?.paid_amount) > 0 && (
                    <div className="paid-pill" title={tr("Оплачено")}>
                      <span className="paid-pill__dot" />
                      <span className="paid-pill__label">
                        {tr("Оплачено")}:
                      </span>
                      <span className="paid-pill__amount">
                        {money(o.paid_amount)}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <ul className="meta">
                <li>👤 {o.cashier}</li>
                {o.time && <li>🕒 {o.time}</li>}
                {o.courier && <li>🚚 {o.courier}</li>}
              </ul>

              <div className="status">
                <span className="status__dot" />
                <span className="status__text">{o.status.label}</span>
              </div>
            </article>
          ))}
        </div>
      )}

      <Modal
        isOpen={showInvoice}
        contentLabel={tr("Счет")}
        style={{
          overlay: { zIndex: 1000, backgroundColor: "rgba(0,0,0,.5)" },
          content: {
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            transform: "translate(-50%, -50%)",
            padding: 0,
            border: "none",
            borderRadius: "16px",
            width: "min(980px, 96vw)",
            height: "min(90vh, 900px)",
            display: "flex",
            flexDirection: "column",
            maxWidth: "900px",
            overflow: "auto",
          },
        }}
      >
        <OrderInvoice
          orderData={orderData}
          onClose={() => setShowInvoice(false)}
        />
      </Modal>
    </div>
  );
};

export default Orders;
